import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { GET_ZOHO_ACCOUNTS, UPDATE_ZOHO_ACCOUNT } from '../utils/constants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const axios = require('axios');

const NewZohoConnection = ({
  open,
  onOpenChange,
  currentClientId,
  currentClient,
  connectSnCallback
}) => {
  const [optionsData, setOptionsData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [zohoAccount, setZohoAccount] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [savingClient, setSavingClient] = useState(false);

  useEffect(() => {
    const getPages = async () => {
      const res = await axios.get(GET_ZOHO_ACCOUNTS).catch((error) => {
        console.error('error ' + error);
        throw new Error(error);
      });

      if (optionsData === null) {
        setOptionsData(res.data.body);
        setLoaded(true);
      }
    };
    if (optionsData === null) {
      getPages();
    }
  }, [optionsData]);

  const handleSelectChanges = (option) => {
    if (option) {
      const Options = {
        id: option.value,
        Account_Name: option.label,
        currentClientId: currentClientId
      };

      setZohoAccount(Options);
      setClientId(currentClientId);
    }
  };

  const zohoConnectionHandler = () => {
    setSavingClient(true);
    if (zohoAccount == null) {
      alert('Please select an account.');
      setSavingClient(false);
      setZohoAccount(null);
      return;
    }

    axios({
      method: 'post',
      url: UPDATE_ZOHO_ACCOUNT,
      data: zohoAccount,
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) {
        if (response.status === 200) {
          setSavingClient(false);
          setOptionsData(null);
          setZohoAccount(null);
          onOpenChange(false);
          someFn(clientId);
        }
      })
      .catch(function (response) {
        setOptionsData(null);
        setZohoAccount(null);
        setSavingClient(false);
      });
  };

  const someFn = (dataForParent) => {
    connectSnCallback(dataForParent);
  };

  const handleClose = () => {
    setOptionsData(null);
    setZohoAccount(null);
    onOpenChange(false);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused
        ? 'hsl(var(--primary))'
        : 'hsl(var(--border))',
      boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      zIndex: 50
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary))'
        : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    })
  };

  if (!loaded) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Connect Zoho Account
            </DialogTitle>
            <DialogDescription>
              Loading accounts for {currentClient?.clientName || 'this client'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Connect Zoho Account
          </DialogTitle>
          <DialogDescription>
            Select the Zoho account for{' '}
            {currentClient?.clientName || 'this client'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Zoho Account Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Zoho CRM Account</Label>
              <span className="text-sm text-muted-foreground">
                {optionsData?.length || 0} accounts available
              </span>
            </div>
            <Select
              options={optionsData?.map((option) => ({
                value: option.id,
                label: option.Account_Name,
                isAssigned: option?.assignedClient
              }))}
              onChange={handleSelectChanges}
              isSearchable
              isClearable
              placeholder="Search for a Zoho account..."
              isLoading={!optionsData}
              styles={customSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
              formatOptionLabel={(option) => (
                <div className="flex items-center gap-2">
                  <span
                    className={
                      option.isAssigned ? 'text-green-600' : 'text-red-600'
                    }>
                    {option.isAssigned ? '🔗' : '🔓'}
                  </span>
                  <span>{option.label}</span>
                </div>
              )}
            />
            <p className="text-xs text-muted-foreground">
              🔗 = Already assigned to another client | 🔓 = Available
            </p>
          </div>

          {/* Success indicator when account is selected */}
          {zohoAccount && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Selected: <strong>{zohoAccount.Account_Name}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={savingClient}>
            Cancel
          </Button>
          <Button
            onClick={zohoConnectionHandler}
            disabled={savingClient || !zohoAccount}
            className="min-w-[120px]">
            {savingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewZohoConnection;
